/**
 * Shared formatting + small helpers for the salience monitor (S5).
 *
 * Honest labelling discipline (plan §1.1) lives here too: a single source for
 * what each agenda's headline metric is, so copy never drifts into calling a
 * share an "importance".
 */
import type {
  SalienceMetricPoint,
  SalienceOverviewTerm,
} from "../../lib/api";

/** A media coverage share → a percent string.
 *
 * IMPORTANT unit note: GDELT shares are ALREADY percentages — both the DOC
 * API's TimelineVol and the BigQuery collector's `raw/total*100` produce
 * percent values (e.g. 0.0236 = 0.0236% of that day's corpus). This function
 * originally assumed a 0–1 fraction and multiplied by 100, which displayed
 * every media share ×100 too high (fixed 2026-06-10). Entity shares live at
 * 0.0001–0.1%, so small values keep 4 decimals.
 */
export function formatShare(share: number | null | undefined): string {
  if (share == null) return "—";
  const pct = share;
  if (pct === 0) return "0%";
  if (pct < 0.0001) return "<0.0001%";
  if (pct < 0.1) return `${pct.toFixed(4)}%`;
  if (pct < 1) return `${pct.toFixed(2)}%`;
  if (pct < 10) return `${pct.toFixed(1)}%`;
  return `${pct.toFixed(0)}%`;
}

export function formatVolume(v: number | null | undefined): string {
  if (v == null) return "—";
  return v.toLocaleString("en-GB");
}

/** A Google Trends search-interest index — a relative 0–100 figure (100 = peak
 * in the queried window), NOT a percentage. Rendered as a bare integer so it's
 * never confused with the media coverage SHARE (which genuinely is a %). */
export function formatIndex(v: number | null | undefined): string {
  if (v == null) return "—";
  return String(Math.round(v));
}

/** Signed velocity for the search index — index points, no unit suffix. */
export function formatIndexVelocity(v: number | null | undefined): string {
  if (v == null || v === 0) return "0";
  const sign = v > 0 ? "+" : "";
  return `${sign}${Math.round(v)}`;
}

/** Signed velocity for a share metric — already in percentage points (the
 * scorer's Δshare, same percent units as the share itself; see formatShare). */
export function formatShareVelocity(v: number | null | undefined): string {
  if (v == null || v === 0) return "0pp";
  const sign = v > 0 ? "+" : "";
  const abs = Math.abs(v);
  const digits = abs < 0.1 ? 4 : 2;
  return `${sign}${v.toFixed(digits)}pp`;
}

export function formatVolumeVelocity(v: number | null | undefined): string {
  if (v == null || v === 0) return "0";
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toLocaleString("en-GB")}`;
}

/** Sign of a value → up / down / flat, for colour + arrow selection. */
export function direction(v: number | null | undefined): "up" | "down" | "flat" {
  if (v == null || v === 0) return "flat";
  return v > 0 ? "up" : "down";
}

export const DIRECTION_CLASS: Record<"up" | "down" | "flat", string> = {
  up: "text-up",
  down: "text-down",
  flat: "text-terminal-ink-muted",
};

export const DIRECTION_ARROW: Record<"up" | "down" | "flat", string> = {
  up: "▲",
  down: "▼",
  flat: "▬",
};

export function shortDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "2-digit",
  });
}

/** "5 Jul 26" from a ts — the WindowNav range-label format (both time charts). */
export function windowDate(ts: number): string {
  if (!Number.isFinite(ts)) return "—";
  return new Date(ts).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "2-digit",
  });
}

export function dayMonth(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/** "5 Jun '26" — day + month + apostrophe-year. The apostrophe keeps the year
 * from reading as a day number ("5 Jun 26" is ambiguous). */
export function dayMonthYear(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  const yy = String(d.getUTCFullYear()).slice(2);
  return `${d.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} '${yy}`;
}

/** "Jun '26" — month + apostrophe-year, for long-range axis ticks. */
export function monthYear(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  const yy = String(d.getUTCFullYear()).slice(2);
  return `${d.toLocaleDateString("en-GB", { month: "short" })} '${yy}`;
}

/**
 * Window-aware x-axis tick formatter. Year-less "5 Jun" ticks were ambiguous
 * on long-term views (the media series reaches back to 2019), so:
 *   • window > ~10 months → "Jun '25" (month granularity, year always shown)
 *   • window crosses a calendar-year boundary → "5 Jun '25"
 *   • short single-year window → "5 Jun" (unchanged)
 */
export function makeTimeTickFormatter(
  minTs: number,
  maxTs: number,
): (ts: number) => string {
  if (!Number.isFinite(minTs) || !Number.isFinite(maxTs) || maxTs <= minTs) {
    return (ts) => dayMonth(new Date(ts).toISOString());
  }
  const spanDays = (maxTs - minTs) / 86_400_000;
  const crossesYear =
    new Date(minTs).getUTCFullYear() !== new Date(maxTs).getUTCFullYear();
  if (spanDays > 300) return (ts) => monthYear(new Date(ts).toISOString());
  if (crossesYear) return (ts) => dayMonthYear(new Date(ts).toISOString());
  return (ts) => dayMonth(new Date(ts).toISOString());
}

/** ms-epoch for a YYYY-MM-DD bucket (UTC midnight) — the chart x domain. */
export function bucketTs(iso: string): number {
  return new Date(`${iso}T00:00:00Z`).getTime();
}

/**
 * Which FIELD an agenda's value lives in. media (GDELT coverage %) and search
 * (Trends 0–100 index) both store their normalised value in `share`; public and
 * elite headline on VOLUME (public's within-sample share is 1÷1 = 100% for a
 * low-volume entity and its velocity is a volume delta, so a % is misleading —
 * count of relevant items is the honest number). NOTE this says where to READ
 * the value, not how to FORMAT it — media renders as %, search as a bare index
 * (see formatAgendaValue), so don't infer the unit from this alone.
 */
export function agendaMetric(agenda: string): "share" | "volume" {
  return agenda === "media" || agenda === "search" ? "share" : "volume";
}

/** Pull a single value (share/index or volume) from a metric point. */
export function metricValue(p: SalienceMetricPoint, agenda: string): number | null {
  return agendaMetric(agenda) === "share" ? p.share : p.volume;
}

/** Format a metric value for display — agenda-aware so the unit is correct:
 * media → coverage %, search → bare 0–100 index, public/elite → count. */
export function formatAgendaValue(v: number | null | undefined, agenda: string): string {
  if (agenda === "search") return formatIndex(v);
  return agendaMetric(agenda) === "share" ? formatShare(v) : formatVolume(v);
}

/** Format a metric velocity for display — agenda-aware (pp / index pts / count). */
export function formatAgendaVelocity(v: number | null | undefined, agenda: string): string {
  if (agenda === "search") return formatIndexVelocity(v);
  return agendaMetric(agenda) === "share"
    ? formatShareVelocity(v)
    : formatVolumeVelocity(v);
}

/**
 * Per-agenda freshness cadence, in days. A series whose latest bucket is older
 * than its agenda's cadence is stale enough to badge on the tiles — the header
 * "scored 2h ago" only says the scorer ran, not that the underlying collection
 * is current (see the 2026-07-03 viz review §2). Media/public update ~daily, so
 * >2d means a missed cycle; search is the ~weekly Trends pull (>8d); elite
 * (Parliament / petitions) publishes on sitting days, so allow a long weekend.
 */
export const AGENDA_STALE_DAYS: Record<string, number> = {
  media: 2,
  public: 2,
  search: 8,
  elite: 4,
};
const DEFAULT_STALE_DAYS = 3;

/** Whole/fractional days between an ISO date (YYYY-MM-DD or full ISO) and now,
 * or null if unparseable. */
export function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return null;
  return (Date.now() - d.getTime()) / 86_400_000;
}

/** Is a series' latest bucket older than its agenda's freshness cadence? */
export function isStale(agenda: string, latestDate: string | null | undefined): boolean {
  const age = daysSince(latestDate);
  if (age == null) return false;
  return age > (AGENDA_STALE_DAYS[agenda] ?? DEFAULT_STALE_DAYS);
}

/**
 * Trailing N-calendar-day moving average of each key across ts-sorted rows.
 *
 * Raw daily GDELT share is spiky noise at 0.001–0.1% (and the BQ benchmarks
 * already reason in ~3-day-smoothed terms), so the chart offers this as an
 * opt-in trend view. It preserves the ts set (x-domain + markers stay put) and
 * only rewrites the per-geo values; gaps are handled by averaging just the
 * points actually present inside the window, so a missing day doesn't zero the
 * mean. `rows` must be sorted ascending by ts.
 */
export function rollingMean(
  rows: Array<Record<string, number>>,
  keys: string[],
  windowDays: number,
): Array<Record<string, number>> {
  const winMs = (windowDays - 1) * 86_400_000;
  return rows.map((row, i) => {
    const out: Record<string, number> = { ts: row.ts };
    for (const k of keys) {
      let sum = 0;
      let n = 0;
      for (let j = i; j >= 0; j--) {
        if (rows[j].ts < row.ts - winMs) break;
        const v = rows[j][k];
        if (v != null) {
          sum += v;
          n += 1;
        }
      }
      if (n > 0) out[k] = sum / n;
    }
    return out;
  });
}

/**
 * Densify sparse per-day VOLUME series with explicit zeros.
 *
 * Item-level agendas (elite; public·YouTube) only get a metric bucket on days
 * with ≥1 relevant item, so a line chart interpolates across the gaps — six
 * scattered single-item June days rendered as a solid six-week plateau at 1
 * (the 2026-07 Elite flat-line bug). For a count-per-day series the honest
 * reading of a missing day inside the collected span is ZERO.
 *
 * Fills each key independently from its first non-null day, stepping one UTC
 * day at a time, through `fillEndTs` (or just through its own last non-null
 * day when `fillEndTs` is omitted — interior gaps only). Other keys on shared
 * rows are left untouched. Returns ts-ascending rows.
 */
export function zeroFillDaily(
  rows: Array<Record<string, number>>,
  keys: string[],
  fillEndTs?: number,
): Array<Record<string, number>> {
  const byTs = new Map<number, Record<string, number>>();
  for (const r of rows) byTs.set(r.ts, r);
  for (const k of keys) {
    let first = Infinity;
    let last = -Infinity;
    for (const r of rows)
      if (r[k] != null) {
        if (r.ts < first) first = r.ts;
        if (r.ts > last) last = r.ts;
      }
    if (first === Infinity) continue;
    const end = Math.max(fillEndTs ?? last, last);
    for (let t = first; t <= end; t += 86_400_000) {
      const row = byTs.get(t) ?? { ts: t };
      if (row[k] == null) row[k] = 0;
      byTs.set(t, row);
    }
  }
  return [...byTs.values()].sort((a, b) => a.ts - b.ts);
}

/** The geo lines a given agenda exposes (matches the scorer's geo sets). */
export function geosForAgenda(agenda: string): string[] {
  return agenda === "media" || agenda === "search"
    ? ["worldwide", "UK", "US"]
    : ["worldwide", "GB"];
}

export const GEO_LABELS: Record<string, string> = {
  worldwide: "Worldwide",
  UK: "UK",
  GB: "UK",
  US: "US",
};

/** Distinct line colours per geo within one agenda chart. */
export const GEO_COLOURS: Record<string, string> = {
  worldwide: "#4f8cff",
  UK: "#16c784",
  GB: "#16c784",
  US: "#f5a524",
};

export const EVENT_TYPE_LABELS: Record<string, string> = {
  budget: "Budget / fiscal",
  election: "Election",
  report: "Report",
  policy_event: "Policy event",
  news_event: "News event",
};

export const EVENT_TYPE_COLOURS: Record<string, string> = {
  budget: "#f5a524",
  election: "#ea3943",
  report: "#c084fc",
  policy_event: "#16c784",
  news_event: "#9aa0d8",
};

/**
 * Pick a term's headline (agenda, geo, value, velocity) for the watchlist /
 * tiles — prefer the agenda where it has the strongest live signal. Topics
 * lead on media share; entities often only show up in public volume; fall back
 * down the ladder so every tracked term shows *something*.
 */
export interface Headline {
  agenda: string;
  geo: string;
  metric: "share" | "volume";
  value: number | null;
  velocity: number | null;
  date: string;
}

export function termHeadline(term: SalienceOverviewTerm): Headline | null {
  const tryOrder: Array<[string, string]> = [
    ["media", "worldwide"],
    ["media", "UK"],
    ["search", "worldwide"],
    ["search", "UK"],
    ["public", "worldwide"],
    ["elite", "GB"],
    ["media", "US"],
  ];
  const candidates: Headline[] = [];
  for (const [agenda, geo] of tryOrder) {
    const g = term.agendas[agenda]?.[geo];
    if (!g) continue;
    const metric = agendaMetric(agenda);
    const value = metric === "share" ? g.latest_share : g.latest_volume;
    if (value == null) continue;
    candidates.push({ agenda, geo, metric, value, velocity: g.velocity, date: g.latest_date });
  }
  // Prefer the first agenda in ladder order that actually has a live signal
  // (>0) — a 0% media share is present but uninformative for entities that
  // only surface in public volume. Fall back to the first present value.
  return candidates.find((c) => (c.value ?? 0) > 0) ?? candidates[0] ?? null;
}

export function formatHeadlineValue(h: Headline): string {
  return formatAgendaValue(h.value, h.agenda);
}

export function formatHeadlineVelocity(h: Headline): string {
  return formatAgendaVelocity(h.velocity, h.agenda);
}
